import React, { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import svgIcons from "../../assets/svg/svgIcons";

interface FTModalProps {
  children: React.ReactNode;
  containerStyle?: string;
  CustomHeadingWrapper?: (Heading: ReactNode | string) => ReactNode,
  dismissible?: boolean;
  extraWrapperClass?: string;
  heading?: ReactNode | string;
  onHide: () => void;
  position?: 'top' | 'center';
  positionHorizontal?: 'left' | 'center' | 'right';
  visible: boolean;
  isBuzzerAlert?: boolean;
  nodeLeftOfCloseButton?: React.ReactNode;
  disableOverlay?:boolean;
  renderAboveImpersonationBar?: boolean;
}

const FTModal = ({ dismissible = true, ...props }: FTModalProps) => {
  if (!props.visible) return null;

  const zOverlay = props?.renderAboveImpersonationBar
    ? 'z-overlay-above-impersonation-bar'
    : props?.isBuzzerAlert
      ? 'z-overlay-buzzer'
      : 'z-overlay';
  const zModal = props?.renderAboveImpersonationBar
    ? 'z-modal-above-impersonation-bar'
    : props?.isBuzzerAlert
      ? 'z-modal-buzzer'
      : 'z-modal';
  const pos = props?.position === 'center' ? 'my-auto' : 'my-6';
  const posHorizontal = props?.positionHorizontal === 'right' ? 'right-6 absolute' : props?.positionHorizontal === 'left' ? 'left-10 absolute' : 'mx-auto relative';
  const contStyle = props?.containerStyle ? props.containerStyle : "max-w-[calc(100vw-4rem)] w-[560px]";

  const HeadingWrapper = !!props.CustomHeadingWrapper
    ? props.CustomHeadingWrapper
    : (Heading: ReactNode | string) => (
      <>
        {!!props.heading && (
          <div className="text-2xl font-bold text-heading-black">
            {Heading}
          </div>
        )}
      </>
    );

  // Render through a portal to document.body so the modal escapes any ancestor
  // CSS stacking context (e.g. the reports search-results dropdown uses `z-10`
  // + positioning, which would otherwise trap the modal's z-index and push it
  // beneath the impersonation bar). Portaling keeps the z-index evaluated at the
  // document root so `renderAboveImpersonationBar` behaves consistently across
  // every entry point.
  const modalContent = (
    <div className={`group/ftmodal justify-center items-start flex overflow-x-hidden overflow-y-auto fixed inset-0 ${zOverlay} outline-none focus:outline-none`}>
      <div className={`fixed w-full h-dvh bg-modal-overlay ${zOverlay}`} onClick={dismissible && !props?.disableOverlay ? props.onHide : undefined}></div>
      <div className={`${zModal} ${posHorizontal} ${pos} ${contStyle} ${props?.extraWrapperClass ?? ''}`}>
        <div className="p-8 bg-white rounded-xl shadow-2xl">
          {!!props.heading && ( // !!dismissible && (
            <div className="flex flex-row justify-between mb-4">
              {HeadingWrapper(props.heading)}
              <div className={`flex ${!!props.CustomHeadingWrapper ? 'items-start' : 'items-center'}`}>
                {props.nodeLeftOfCloseButton ?? null}
                {!!dismissible && (
                  <button type="button" className="no-print flex justify-end" onClick={props.onHide}>
                    <img src={svgIcons.CloseIcon} alt="close" className="size-8" />
                  </button>
                )}
              </div>
            </div>
          )}
          {
            props.children
          }
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default FTModal;